import { FC, useMemo } from 'react';
import { EmbeddedMessage, MessageMedium, WhatsApp } from 'constants/messageMediums';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { useUserMetadataQuery } from '../../hooks/useUserMetadataQuery';
import { useUserKey } from '../../hooks';
import { Channel, MessageType, ChannelType, SubscriptionPolicy } from '../../domain';
import { applyFilters } from '../utils/applyFilters';
import { ExtendedChannel, ExtendedMessageType } from '../types';
import { QueryContext } from './QueryContext';
import { useFiltersContext } from './FiltersContext';

const DEFAULT_CHANNELS: Channel[] = [];
const DEFAULT_MESSAGE_TYPES: MessageType[] = [];

const isChannelSubscribed = (
  channel: Channel['id'],
  unsubscribedChannels: number[] = [],
): boolean => !unsubscribedChannels.includes(channel);

const isMarketingChannel = ({ channelType }: Channel) => channelType === ChannelType.marketing;

const isMessageSubscribed = (
  message: MessageType,
  subscribedMessages: number[] = [],
  unsubscribedMessages: number[] = [],
): boolean => {
  if (
    message.subscriptionPolicy === SubscriptionPolicy.optIn ||
    message.subscriptionPolicy === SubscriptionPolicy.doubleOptIn
  ) {
    return subscribedMessages.includes(message.id);
  }
  return !unsubscribedMessages.includes(message.id);
};

const isMessagePendingSubscription = (
  message: MessageType,
  pendingSubscriptionsMessageTypeIds: number[] = [],
) => {
  if (message.subscriptionPolicy === SubscriptionPolicy.doubleOptIn) {
    return !!pendingSubscriptionsMessageTypeIds?.includes(message.id);
  }
  return false;
};

export const QueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const userKey = useUserKey();
  const { search, subscriptionPolicies, subscriptionStatuses, mediums } = useFiltersContext();
  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();

  const {
    isLoading,
    isError,
    data: {
      channels: rawChannels = DEFAULT_CHANNELS,
      messageTypes: rawMessageTypes = DEFAULT_MESSAGE_TYPES,
      pendingSubscriptionsMessageTypeIds,
      subscribedMessageTypeIds,
      unsubscribedChannelIds,
      unsubscribedMessageTypeIds,
    } = {},
  } = useUserMetadataQuery({
    userKey,
  });

  const channels: ExtendedChannel[] = useMemo(
    () =>
      rawChannels.map((channel) => ({
        ...channel,
        isSubscribed: isChannelSubscribed(channel.id, unsubscribedChannelIds),
      })),
    [rawChannels, unsubscribedChannelIds],
  );

  const messageTypes: ExtendedMessageType[] = useMemo(
    () =>
      rawMessageTypes.map((messageType) => ({
        ...messageType,
        isSubscribed: isMessageSubscribed(
          messageType,
          subscribedMessageTypeIds,
          unsubscribedMessageTypeIds,
        ),
        isPendingSubscription: isMessagePendingSubscription(
          messageType,
          pendingSubscriptionsMessageTypeIds,
        ),
        isSubscribedToChannel: isChannelSubscribed(
          messageType.messageChannelId,
          unsubscribedChannelIds,
        ),
      })),
    [
      rawMessageTypes,
      subscribedMessageTypeIds,
      unsubscribedChannelIds,
      unsubscribedMessageTypeIds,
      pendingSubscriptionsMessageTypeIds,
    ],
  );

  const marketingChannels = useMemo(() => channels.filter(isMarketingChannel), [channels]);

  const channelMedia = useMemo(
    () => [
      ...new Set(
        channels
          .map(({ messageMedium }) => messageMedium)
          .filter(
            (messageMedium: MessageMedium) =>
              messageMedium !== EmbeddedMessage &&
              messageMedium !== ('Flex' as unknown as MessageMedium) &&
              (messageMedium !== WhatsApp || (messageMedium === WhatsApp && isWhatsAppFFOn)),
          ),
      ),
    ],
    [channels],
  );

  const { filteredChannels, filteredMessageTypes } = useMemo(
    () =>
      applyFilters({
        channels,
        messageTypes,
        policies: subscriptionPolicies,
        statuses: subscriptionStatuses,
        mediums,
        search,
      }),
    [channels, messageTypes, subscriptionPolicies, subscriptionStatuses, mediums, search],
  );

  return (
    <QueryContext.Provider
      value={{
        channelMedia,
        channels: filteredChannels,
        marketingChannels,
        messageTypes: filteredMessageTypes,
        isLoading,
        isError,
      }}
    >
      {children}
    </QueryContext.Provider>
  );
};
